/**
 * Pure MP3 player engine helpers (no DOM, no Winamp look).
 */
import { SHOP_ITEM_WINAMP } from '../../../services/userProfile';

export { SHOP_ITEM_WINAMP };

/**
 * MapToBe Radio is a shop unlock (SKU `winamp-player`) for non-admins.
 * @param {{ isAdmin?: boolean, shopOwned?: Record<string, boolean> | null }} [opts]
 */
export function canUseMp3Player({ isAdmin = false, shopOwned = null } = {}) {
  return isAdmin === true || Boolean(shopOwned?.[SHOP_ITEM_WINAMP]);
}

/**
 * MediaSession / sticker credit. Unassigned tracks stay MapToBe.
 * @param {{ artistName?: string } | null | undefined} track
 */
export function radioArtistLabel(track) {
  const name = String(track?.artistName || '').trim();
  return name || 'MapToBe';
}

/**
 * @param {{ artistSlug?: string } | null | undefined} track
 * @returns {string}
 */
export function radioArtistHref(track) {
  const slug = String(track?.artistSlug || '').trim();
  return slug ? `/artists/${encodeURIComponent(slug)}` : '';
}

export const HUB_AUDIO_COLLECTION = 'hubAudioTracks';
export const HUB_AUDIO_STORAGE_PREFIX = 'hub-audio';
export const HUB_AUDIO_CLASS = 'hub-mp3-audio';
export const HUB_AUDIO_MAX_BYTES = 20 * 1024 * 1024;
export const STREAM_URL_TTL_MS = 15 * 60 * 1000;
export const STREAM_URL_REFRESH_LEAD_MS = 2 * 60 * 1000;
export const SHADE_STORAGE_KEY = 'm2b-mp3-shade';
export const POSITION_STORAGE_KEY = 'm2b-mp3-position';
export const RADIO_HIDDEN_STORAGE_KEY = 'm2b-radio-hidden';
export const PLAYBACK_STORAGE_KEY = 'm2b-radio-playback';
export const PLAYBACK_PERSIST_MS = 2000;

export function readStoredRadioHidden() {
  try {
    if (typeof localStorage === 'undefined') return false;
    return localStorage.getItem(RADIO_HIDDEN_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function writeStoredRadioHidden(value) {
  try {
    if (typeof localStorage === 'undefined') return;
    if (value) {
      localStorage.setItem(RADIO_HIDDEN_STORAGE_KEY, 'true');
    } else {
      localStorage.removeItem(RADIO_HIDDEN_STORAGE_KEY);
    }
  } catch {
    /* ignore */
  }
}

export const HUB_AUDIO_ACCEPT =
  'audio/mpeg,audio/mp4,audio/aac,audio/ogg,audio/wav,audio/x-m4a,.mp3,.m4a,.aac,.ogg,.wav';

const ALLOWED_TYPES = new Set([
  'audio/mpeg',
  'audio/mp4',
  'audio/aac',
  'audio/ogg',
  'audio/wav',
  'audio/x-m4a',
]);

/**
 * @param {number} index
 * @param {number} length
 * @param {number} delta
 */
export function wrapPlaylistIndex(index, length, delta) {
  if (!length || length < 1) return 0;
  const i = Number(index) || 0;
  const step = Number(delta) || 0;
  return (((i + step) % length) + length) % length;
}

/** Next wrap index for playlist advance (same as wrapPlaylistIndex(…, 1)). */
export function nextPlaylistIndex(index, length) {
  return wrapPlaylistIndex(index, length, 1);
}

/**
 * Refresh signed URLs only while playing, shortly before expiry.
 * @param {{ isPlaying?: boolean, expiresAt?: number, now?: number, leadMs?: number }} opts
 */
export function shouldRefreshStreamUrl({
  isPlaying = false,
  expiresAt = 0,
  now = Date.now(),
  leadMs = STREAM_URL_REFRESH_LEAD_MS,
} = {}) {
  if (!isPlaying) return false;
  const exp = Number(expiresAt) || 0;
  if (!exp) return false;
  return exp - now <= leadMs;
}

/**
 * Whether the standby next-track URL should be minted or refreshed.
 * Warm when missing; refresh while session is active inside the lead window.
 * @param {{
 *   wantPlaying?: boolean,
 *   trackId?: string | null,
 *   url?: string,
 *   expiresAt?: number,
 *   now?: number,
 *   leadMs?: number,
 * }} opts
 */
export function shouldWarmStandby({
  wantPlaying = false,
  trackId = null,
  url = '',
  expiresAt = 0,
  now = Date.now(),
  leadMs = STREAM_URL_REFRESH_LEAD_MS,
} = {}) {
  if (!wantPlaying || !trackId) return false;
  if (!url) return true;
  return shouldRefreshStreamUrl({ isPlaying: true, expiresAt, now, leadMs });
}

/**
 * Resume playback when the tab returns if the user still wants audio
 * but the element is not actually playing (e.g. OS blocked background advance).
 * @param {{ wantPlaying?: boolean, isPlaying?: boolean, documentVisible?: boolean }} opts
 */
export function shouldResumeOnForeground({
  wantPlaying = false,
  isPlaying = false,
  documentVisible = true,
} = {}) {
  return Boolean(wantPlaying && !isPlaying && documentVisible);
}

/**
 * True when standby is ready for a sync handoff to this track id.
 * @param {{
 *   standbyTrackId?: string | null,
 *   standbyUrl?: string,
 *   targetTrackId?: string | null,
 *   expiresAt?: number,
 *   now?: number,
 * }} opts
 */
export function canHandoffFromStandby({
  standbyTrackId = null,
  standbyUrl = '',
  targetTrackId = null,
  expiresAt = 0,
  now = Date.now(),
} = {}) {
  if (!standbyTrackId || !targetTrackId || !standbyUrl) return false;
  if (standbyTrackId !== targetTrackId) return false;
  const exp = Number(expiresAt) || 0;
  if (exp && exp <= now) return false;
  return true;
}

/**
 * @param {string|undefined|null} contentType
 * @param {string|undefined|null} fileName
 */
export function isAllowedHubAudioType(contentType, fileName = '') {
  const type = String(contentType || '').toLowerCase().trim();
  if (ALLOWED_TYPES.has(type)) return true;
  const name = String(fileName || '').toLowerCase();
  return /\.(mp3|m4a|aac|ogg|wav)$/.test(name);
}

/** Winamp-style MM:SS for the LCD. */
export function formatLcdTime(seconds) {
  const s = Math.max(0, Math.floor(Number(seconds) || 0));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
}

/**
 * Keep the floating widget on-screen after drag, above the public bottom nav.
 * @param {{
 *   x: number,
 *   y: number,
 *   width?: number,
 *   height?: number,
 *   viewportW?: number,
 *   viewportH?: number,
 *   bottomInset?: number,
 * }} opts
 */
export function clampPlayerPosition({
  x = 0,
  y = 0,
  width = 280,
  height = 116,
  viewportW = 800,
  viewportH = 600,
  bottomInset = 0,
} = {}) {
  const inset = Math.max(0, Number(bottomInset) || 0);
  const maxX = Math.max(0, viewportW - width);
  const maxY = Math.max(0, viewportH - height - inset);
  return {
    x: Math.min(Math.max(0, x), maxX),
    y: Math.min(Math.max(0, y), maxY),
  };
}

export function readStoredShade(defaultShade = true) {
  if (typeof localStorage === 'undefined') return defaultShade;
  try {
    const raw = localStorage.getItem(SHADE_STORAGE_KEY);
    if (raw === '0') return false;
    if (raw === '1') return true;
  } catch {
    /* ignore */
  }
  return defaultShade;
}

export function writeStoredShade(shade) {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(SHADE_STORAGE_KEY, shade ? '1' : '0');
  } catch {
    /* ignore */
  }
}

/** @returns {{ left: number, top: number } | null} */
export function readStoredPosition() {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(POSITION_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed?.left === 'number' && typeof parsed?.top === 'number') {
      return { left: parsed.left, top: parsed.top };
    }
  } catch {
    /* ignore */
  }
  return null;
}

export function writeStoredPosition(pos) {
  if (typeof localStorage === 'undefined' || !pos) return;
  try {
    localStorage.setItem(
      POSITION_STORAGE_KEY,
      JSON.stringify({ left: pos.left, top: pos.top }),
    );
  } catch {
    /* ignore */
  }
}

/** @returns {{ trackId: string, currentTime: number } | null} */
export function readStoredPlayback() {
  if (typeof localStorage === 'undefined') return null;
  try {
    const raw = localStorage.getItem(PLAYBACK_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    const trackId = String(parsed?.trackId || '').trim();
    const currentTime = Number(parsed?.currentTime);
    if (!trackId || !Number.isFinite(currentTime)) return null;
    return { trackId, currentTime: Math.max(0, currentTime) };
  } catch {
    return null;
  }
}

export function writeStoredPlayback(pos) {
  if (typeof localStorage === 'undefined' || !pos) return;
  const trackId = String(pos.trackId || '').trim();
  const currentTime = Number(pos.currentTime);
  if (!trackId || !Number.isFinite(currentTime)) return;
  try {
    localStorage.setItem(
      PLAYBACK_STORAGE_KEY,
      JSON.stringify({ trackId, currentTime: Math.max(0, currentTime) }),
    );
  } catch {
    /* ignore */
  }
}

export function clearStoredPlayback() {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(PLAYBACK_STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * Map stored track + time onto the current playlist.
 * Missing track → index 0 / 0:00. Time at or past duration → 0:00 on that track.
 * @param {Array<{ id?: string, durationSec?: number }>} tracks
 * @param {{ trackId?: string, currentTime?: number } | null} stored
 * @returns {{ index: number, currentTime: number }}
 */
export function resolveRestoredPlayback(tracks, stored) {
  const list = Array.isArray(tracks) ? tracks : [];
  if (!list.length) return { index: 0, currentTime: 0 };
  const trackId = String(stored?.trackId || '').trim();
  const idx = trackId ? list.findIndex((t) => t?.id === trackId) : -1;
  if (idx < 0) return { index: 0, currentTime: 0 };
  let time = Math.max(0, Number(stored?.currentTime) || 0);
  const dur = Number(list[idx]?.durationSec);
  if (Number.isFinite(dur) && dur > 0) {
    time = time >= dur ? 0 : Math.min(time, dur);
  }
  return { index: idx, currentTime: time };
}

/** True when the callable is not deployed yet (avoid error-popup spam). */
export function isMissingCallableError(err) {
  const code = String(err && err.code ? err.code : '');
  const msg = String(err && err.message ? err.message : '');
  return (
    code === 'functions/not-found'
    || code === 'functions/unimplemented'
    || /not[- ]found|NOT_FOUND|unimplemented/i.test(code)
    || /function.*not (found|exist)|NOT_FOUND/i.test(msg)
  );
}
